import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import PhotoUploader from "../components/PhotoUploader";
import { CHECKLIST_TYPE_LABEL } from "../lib/constants";
import { ArrowLeft, Check, X, Minus, HandMetal, UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ELIGIBLE = {
  maintenance: ["vask", "service", "admin"],
  checkin: ["service", "admin"],
  checkout: ["service", "admin"],
};

export default function ChecklistFillout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cl, setCl] = useState(null);
  const [tpl, setTpl] = useState([]);
  const [boat, setBoat] = useState(null);
  const [responses, setResponses] = useState({});
  const [overall, setOverall] = useState("");
  const [saving, setSaving] = useState(false);
  const [prev, setPrev] = useState(null);

  useEffect(() => {
    (async () => {
      const c = await api.get(`/checklists/${id}`);
      setCl(c.data);
      const templates = await api.get("/checklist-templates");
      const items = templates.data[c.data.type] || [];
      setTpl(items);
      const b = await api.get(`/boats/${c.data.boat_id}`);
      setBoat(b.data);
      // pre-fill from existing responses
      const map = {};
      (c.data.responses || []).forEach((r) => (map[r.key] = r));
      // fill defaults
      items.forEach((it) => {
        if (!map[it.key]) map[it.key] = { key: it.key, photo_ids: [] };
      });
      setResponses(map);
      setOverall(c.data.overall_note || "");
      // previous matching checklist for inline comparison (mainly useful on checkout)
      try {
        const p = await api.get(`/checklists/${id}/previous`);
        setPrev(p.data || null);
      } catch {
        setPrev(null);
      }
    })();
  }, [id]);

  const prevByKey = useMemo(() => {
    const m = {};
    (prev?.responses || []).forEach((r) => (m[r.key] = r));
    return m;
  }, [prev]);

  const readOnly = useMemo(() => cl?.status === "completed", [cl]);

  // Claim model: open pool checklists have assigned_to=null. Anyone with an eligible
  // role may "Ta denne". The holder (or admin) can release it back. Submitting is
  // soft-locked behind a claim so the system always knows who's responsible.
  const isHolder = cl && cl.assigned_to === user.id;
  const isOpen = cl && !cl.assigned_to;
  const canClaim = cl && isOpen && (ELIGIBLE[cl.type] || []).includes(user.role) && !readOnly;
  const isTakenByOther = cl && cl.assigned_to && cl.assigned_to !== user.id;
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    api.get("/users/directory").then((r) => setStaff(r.data)).catch(() => {});
  }, []);

  const holderName = (() => {
    if (!cl?.assigned_to || isHolder) return "";
    const u = staff.find((x) => x.id === cl.assigned_to);
    return u ? `${u.name} (${u.role})` : "en annen";
  })();

  const onClaim = async () => {
    try {
      const { data } = await api.post(`/checklists/${id}/claim`);
      setCl(data);
      toast.success("Du har tatt denne sjekklisten");
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("En annen rakk å ta den først – siden oppdateres");
        const r = await api.get(`/checklists/${id}`);
        setCl(r.data);
      } else {
        toast.error("Kunne ikke ta sjekklisten");
      }
    }
  };

  const onRelease = async () => {
    try {
      const { data } = await api.post(`/checklists/${id}/release`);
      setCl(data);
      toast("Sjekklisten er sluppet tilbake til puljen");
    } catch {
      toast.error("Kunne ikke slippe sjekklisten");
    }
  };

  // Pretty-prints a previous response for inline display, e.g. "OK", "Avvik (manglet)",
  // "6", "ute", "Ikke nødvendig" or null when there's nothing meaningful to show.
  const formatPrev = (r) => {
    if (!r) return null;
    if (r.value) return String(r.value);
    if (r.count != null) return String(r.count);
    if (r.status === "ok") return "OK";
    if (r.status === "not_ok") return r.note ? `Avvik: ${r.note}` : "Avvik";
    if (r.status === "na") return "N/A";
    if (r.status === "not_needed") return "Ikke nødvendig";
    return null;
  };

  const setItem = (key, patch) => {
    setResponses((prev) => ({ ...prev, [key]: { ...prev[key], key, ...patch } }));
  };

  // Partial-save model: anyone with the right role can edit items, save progress,
  // and someone else can finalize later. We no longer auto-claim on submit – a claim
  // is now a soft "I'm working on this right now" indicator, not a hard lock.
  const submitFlow = async ({ partial }) => {
    setSaving(true);
    try {
      const payload = {
        responses: tpl.map((it) => responses[it.key] || { key: it.key, photo_ids: [] }),
        overall_note: overall,
        partial,
      };
      const { data } = await api.post(`/checklists/${id}/submit`, payload);
      if (partial) {
        setCl(data);
        // re-hydrate responses with the merged server view so per-item attribution shows up
        const map = {};
        (data.responses || []).forEach((r) => (map[r.key] = r));
        tpl.forEach((it) => { if (!map[it.key]) map[it.key] = { key: it.key, photo_ids: [] }; });
        setResponses(map);
        toast.success("Fremdrift lagret");
      } else {
        toast.success("Sjekkliste fullført");
        navigate(-1);
      }
    } catch {
      toast.error("Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const onSavePartial = () => submitFlow({ partial: true });
  const onSubmit = () => submitFlow({ partial: false });

  if (!cl || !boat) return null;

  return (
    <div className="space-y-6 pb-32">
      <Link to=".." relative="path" className="inline-flex items-center gap-2 text-sm text-[#2B3F4D]">
        <ArrowLeft className="w-4 h-4" /> Tilbake
      </Link>

      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">
          {CHECKLIST_TYPE_LABEL[cl.type]}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">{boat.name}</h1>
        <p className="text-sm text-[#2B3F4D] mt-1">
          Opprettet {new Date(cl.created_at).toLocaleString("nb-NO")}
          {cl.completed_at && ` · Fullført ${new Date(cl.completed_at).toLocaleString("nb-NO")} av ${cl.performed_by_name}`}
        </p>
      </div>

      {!readOnly && (isOpen || isHolder || isTakenByOther) && (
        <Card
          className={`p-4 border-l-4 ${
            isHolder
              ? "border-[#1DA3B3] bg-[#1DA3B3]/5"
              : isTakenByOther
              ? "border-[#BD984D] bg-[#BD984D]/5"
              : "border-emerald-500 bg-emerald-50"
          }`}
          data-testid="cl-claim-banner"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1 flex items-start gap-3">
              {isHolder ? (
                <UserCheck className="w-5 h-5 text-[#1DA3B3] flex-shrink-0 mt-0.5" />
              ) : isTakenByOther ? (
                <UserCheck className="w-5 h-5 text-[#A57F39] flex-shrink-0 mt-0.5" />
              ) : (
                <UserPlus className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="text-sm text-[#0C1824]">
                {isHolder && (
                  <>
                    <div className="font-semibold">Du jobber med denne nå.</div>
                    <p className="text-[#2B3F4D] mt-0.5">
                      Andre ser at du er på saken. Du kan lagre fremdrift underveis, og fullføre
                      når alt er gjort.
                    </p>
                  </>
                )}
                {isTakenByOther && (
                  <>
                    <div className="font-semibold">{holderName || "En annen"} jobber med denne nå.</div>
                    <p className="text-[#2B3F4D] mt-0.5">
                      Du kan fortsatt bidra med punktene du har mulighet til – trykk «Lagre
                      fremdrift». Den som tok den ferdigstiller når alt er klart.
                    </p>
                  </>
                )}
                {isOpen && (
                  <>
                    <div className="font-semibold">Åpen pulje – ingen jobber med denne ennå.</div>
                    <p className="text-[#2B3F4D] mt-0.5">
                      Trykk «Jeg jobber med denne» hvis du tar ansvar, eller bare fyll ut og
                      lagre fremdrift med en gang.
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              {canClaim && (
                <Button
                  onClick={onClaim}
                  data-testid="cl-claim-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <HandMetal className="w-4 h-4 mr-1.5" /> Jeg jobber med denne
                </Button>
              )}
              {isHolder && (
                <Button
                  onClick={onRelease}
                  variant="outline"
                  data-testid="cl-release-btn"
                  className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10"
                >
                  Slipp til puljen
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {prev && cl.type === "checkout" && (
        <Card
          className="p-4 border-l-4 border-[#1DA3B3] bg-[#1DA3B3]/5"
          data-testid="cl-prev-info"
        >
          <div className="text-xs uppercase tracking-[0.18em] text-[#1DA3B3] font-bold">
            Sammenlign med innsjekk
          </div>
          <div className="text-sm text-[#0C1824] mt-1">
            Forrige innsjekk ble fullført{" "}
            <span className="font-semibold">
              {new Date(prev.completed_at).toLocaleString("nb-NO")}
            </span>{" "}
            av <span className="font-semibold">{prev.performed_by_name}</span>. Verdier
            registrert da vises som hint under hvert punkt – sjekk at f.eks. slusefender, tau og
            puter fortsatt stemmer.
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {tpl.map((it) => {
          const r = responses[it.key] || {};
          const isCount = it.count;
          const isText = it.text;
          const isOptions = Array.isArray(it.options) && it.options.length > 0;
          const prevResp = prevByKey[it.key];
          const prevLabel = formatPrev(prevResp);
          return (
            <Card key={it.key} className="p-4" data-testid={`cl-item-${it.key}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium text-[#0C1824]">{it.label_no}</div>
                  {it.expected != null && (
                    <div className="text-xs text-[#A57F39] mt-0.5">Forventet: {it.expected}</div>
                  )}
                  {prevLabel && (
                    <div
                      className="text-xs text-[#2B3F4D] mt-1 inline-flex items-center gap-1.5"
                      data-testid={`cl-${it.key}-prev`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1DA3B3]" />
                      Forrige {prev?.type === "checkin" ? "innsjekk" : "utsjekk"}: <span className="font-semibold text-[#0C1824]">{prevLabel}</span>
                    </div>
                  )}
                  {r.set_by_name && (
                    <div
                      className="text-[10px] text-[#A57F39] mt-1"
                      data-testid={`cl-${it.key}-attribution`}
                    >
                      Satt av <span className="font-semibold">{r.set_by_name}</span>{" "}
                      {r.set_at && new Date(r.set_at).toLocaleString("nb-NO", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </div>
                  )}
                </div>

                {isOptions && (
                  <ToggleGroup
                    type="single"
                    value={r.value || ""}
                    onValueChange={(v) => v && setItem(it.key, { value: v, status: "ok" })}
                    disabled={readOnly}
                    className="border rounded-lg p-1"
                  >
                    {it.options.map((opt) => (
                      <ToggleGroupItem
                        key={opt}
                        value={opt}
                        data-testid={`cl-${it.key}-opt-${opt}`}
                        className="h-9 data-[state=on]:bg-[#1DA3B3] data-[state=on]:text-white px-4 capitalize"
                      >
                        {opt}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                )}

                {!isCount && !isText && !isOptions && (
                  <ToggleGroup
                    type="single"
                    value={r.status || ""}
                    onValueChange={(v) => v && setItem(it.key, { status: v })}
                    disabled={readOnly}
                    className="border rounded-lg p-1"
                  >
                    <ToggleGroupItem value="ok" data-testid={`cl-${it.key}-ok`} className="h-9 data-[state=on]:bg-[#1DA3B3] data-[state=on]:text-white px-3">
                      <Check className="w-4 h-4" /> OK
                    </ToggleGroupItem>
                    <ToggleGroupItem value="not_ok" data-testid={`cl-${it.key}-not_ok`} className="h-9 data-[state=on]:bg-red-600 data-[state=on]:text-white px-3">
                      <X className="w-4 h-4" /> Avvik
                    </ToggleGroupItem>
                    <ToggleGroupItem value="na" data-testid={`cl-${it.key}-na`} className="h-9 data-[state=on]:bg-slate-300 px-3">
                      <Minus className="w-4 h-4" /> N/A
                    </ToggleGroupItem>
                  </ToggleGroup>
                )}
              </div>

              {isCount && (
                <div className="mt-3">
                  <Input
                    type="number"
                    min={0}
                    value={r.count ?? ""}
                    onChange={(e) => setItem(it.key, { count: parseInt(e.target.value, 10) || 0, status: parseInt(e.target.value, 10) === it.expected ? "ok" : "not_ok" })}
                    disabled={readOnly}
                    placeholder={`Antall (forventet ${it.expected})`}
                    data-testid={`cl-${it.key}-count`}
                  />
                </div>
              )}

              {isText && (
                <div className="mt-3">
                  <Textarea
                    value={r.value || ""}
                    onChange={(e) => setItem(it.key, { value: e.target.value, status: e.target.value ? "ok" : null })}
                    disabled={readOnly}
                    rows={2}
                    placeholder="Skriv her…"
                    data-testid={`cl-${it.key}-text`}
                  />
                </div>
              )}

              {(r.status === "not_ok" || it.photo_on_fail) && (
                <div className="mt-3 space-y-2">
                  <Textarea
                    value={r.note || ""}
                    onChange={(e) => setItem(it.key, { note: e.target.value })}
                    disabled={readOnly}
                    rows={2}
                    placeholder="Notat / beskrivelse"
                    data-testid={`cl-${it.key}-note`}
                  />
                  {!readOnly && (
                    <PhotoUploader
                      photoIds={r.photo_ids || []}
                      onChange={(ids) => setItem(it.key, { photo_ids: ids })}
                      label="Bilder"
                    />
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="p-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39] mb-2">
          Overordnet notat
        </div>
        <Textarea
          value={overall}
          onChange={(e) => setOverall(e.target.value)}
          disabled={readOnly}
          rows={3}
          data-testid="cl-overall-note"
        />
      </Card>

      {!readOnly && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white border-t border-[#E2E8F0] p-4 z-30">
          <div className="max-w-[1400px] flex justify-end gap-2">
            <Button
              onClick={onSavePartial}
              disabled={saving}
              variant="outline"
              data-testid="cl-save-partial"
              className="border-[#1DA3B3] text-[#1DA3B3] hover:bg-[#1DA3B3]/10 h-11 px-6"
            >
              {saving ? "Lagrer…" : "Lagre fremdrift"}
            </Button>
            <Button
              onClick={onSubmit}
              disabled={saving}
              data-testid="cl-submit"
              className="bg-[#BD984D] hover:bg-[#A57F39] text-white h-11 px-8"
            >
              {saving ? "Lagrer…" : "Fullfør sjekkliste"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
